import { useEffect, useState } from "react";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import Reveal from "../components/Reveal";
import Modal from "../components/Modal";
import gallery from "../data/gallery";

const GALLERY_BASE = `${process.env.PUBLIC_URL}/gallery/`;

export default function Gallery() {
  const [activeIndex, setActiveIndex] = useState(null);
  const active = activeIndex !== null ? gallery[activeIndex] : null;

  useEffect(() => {
    if (activeIndex === null || gallery.length === 0) return undefined;
    const onKey = (e) => {
      if (e.key === "ArrowRight") setActiveIndex((i) => (i + 1) % gallery.length);
      if (e.key === "ArrowLeft") setActiveIndex((i) => (i - 1 + gallery.length) % gallery.length);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [activeIndex]);

  return (
    <>
      <Nav />

      <div className="mx-auto max-w-content px-6 pb-14 pt-32 md:px-12 md:pb-20 md:pt-40">
        <p className="eyebrow">Community &amp; Outreach</p>
        <h1 className="mt-3 font-serif text-4xl font-extrabold leading-tight text-heading md:text-5xl">
          Gallery
        </h1>
        <p className="mt-5 max-w-[52ch] text-[0.95rem] leading-relaxed text-ink-soft">
          Moments from the workshops, community visits, and outreach days, the places this work has actually
          taken me. Select any photo for a full-size view.
        </p>
      </div>

      {gallery.length > 0 && (
        <div className="pb-24">
          <div className="columns-2 gap-0 sm:columns-3 lg:columns-4">
            {gallery.map((photo, i) => (
              <Reveal
                as="button"
                key={photo.id}
                delay={Math.min(i, 12) * 40}
                onClick={() => setActiveIndex(i)}
                className="group relative mb-0 block w-full overflow-hidden break-inside-avoid"
              >
                <img
                  src={GALLERY_BASE + photo.file}
                  alt={photo.caption}
                  loading="lazy"
                  decoding="async"
                  className="block w-full transition-transform duration-500 group-hover:scale-105"
                />
                <span className="pointer-events-none absolute inset-0 bg-forest-900/0 transition-colors group-hover:bg-forest-900/15" />
                {photo.hasCaption && (
                  <span className="pointer-events-none absolute inset-x-0 bottom-0 translate-y-full bg-forest-900/80 px-3 py-2 text-left text-[0.72rem] uppercase tracking-wide text-white transition-transform duration-300 group-hover:translate-y-0">
                    {photo.caption}
                  </span>
                )}
              </Reveal>
            ))}
          </div>
        </div>
      )}

      <Modal
        open={active !== null}
        onClose={() => setActiveIndex(null)}
        title={active?.hasCaption ? active.caption : ""}
        footer={active && (
          <>
            {gallery.length > 1 && (
              <div className="mr-auto flex gap-3">
                <button
                  className="border border-line-strong px-4 py-2 text-[0.75rem] uppercase tracking-widest2 text-ink-soft transition-colors hover:border-forest-900 hover:text-heading"
                  onClick={() => setActiveIndex((i) => (i - 1 + gallery.length) % gallery.length)}
                >
                  &larr; Prev
                </button>
                <button
                  className="border border-line-strong px-4 py-2 text-[0.75rem] uppercase tracking-widest2 text-ink-soft transition-colors hover:border-forest-900 hover:text-heading"
                  onClick={() => setActiveIndex((i) => (i + 1) % gallery.length)}
                >
                  Next &rarr;
                </button>
              </div>
            )}
            <a
              href={active ? GALLERY_BASE + active.file : "#"}
              target="_blank"
              rel="noreferrer"
              className="border border-forest-900 bg-forest-900 px-6 py-2.5 text-[0.75rem] uppercase tracking-widest2 text-white transition-colors hover:bg-forest-700"
            >
              Open Full Size &rarr;
            </a>
          </>
        )}
      >
        {active && (
          <figure className="m-0 text-center">
            <img
              src={GALLERY_BASE + active.file}
              alt={active.caption}
              className="max-h-[72vh] w-auto max-w-full object-contain"
            />
            {(active.place || active.date) && (
              <figcaption className="mx-auto mt-4 max-w-[60ch] text-[0.82rem] text-ink-soft">
                {[active.place, active.date].filter(Boolean).join(" · ")}
              </figcaption>
            )}
          </figure>
        )}
      </Modal>

      <Footer />
    </>
  );
}
